// src/pages/info/EditCollectorsDetails.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import CancelEditingModal from '../../../components/Modals/CancelEditingModal';
import Modal from 'react-modal';
import SaveChangesModal from '../../../components/Modals/SaveChangesModal';
import './EditCollectorsDetails.css';
import '../DBHouse/EditHouseDetails.css';
import API_BASE_URL from '../../../config';

Modal.setAppElement('#root');

// Разбор и сборка блоков по маркеру @@
const parseBlocks = raw =>
  raw?.split('@@').map(s => s.trim()).filter(Boolean) || [];
const formatBlocks = parts => parts.join('@@');

// Авто-рост textarea
const autoGrow = el => {
  if (el) {
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }
};

const EditCollectorsDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Ожидаем, что при навигации переданы collectorData и source
  const passedCollector = location.state?.collectorData || null;
  const source = location.state?.source === 'map' ? 'map' : 'db';

  // Состояния для полей коллектора (инициализированы из passedCollector)
  const [year, setYear] = useState(passedCollector?.year || '');
  const [name, setName] = useState(passedCollector?.name || '');
  const [material, setMaterial] = useState(passedCollector?.material || '');
  const [length, setLength] = useState(passedCollector?.length || '');
  const [expenditure, setExpenditure] = useState(passedCollector?.expenditure || '');

  // Управление "Конструктивным решением" (design)
  const [designOptions, setDesignOptions] = useState([]);
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [designData, setDesignData] = useState(null);

  // Состояния для блочных полей
  const [diameterBlocks, setDiameterBlocks] = useState(() => {
    const initial = parseBlocks(
      Array.isArray(passedCollector?.diameter)
        ? passedCollector.diameter.join('@@')
        : passedCollector?.diameter
    );
    return initial.length > 0 ? initial : [''];
  });
  const [nodeBlocks, setNodeBlocks] = useState(() => {
    const initial = parseBlocks(
      Array.isArray(passedCollector?.node_flow)
        ? passedCollector.node_flow.join('@@')
        : passedCollector?.node_flow
    );
    return initial.length > 0 ? initial : [''];
  });
  const [conditionBlocks, setConditionBlocks] = useState(() => {
    const initial = parseBlocks(
      Array.isArray(passedCollector?.technical_condition)
        ? passedCollector.technical_condition.join('@@')
        : passedCollector?.technical_condition
    );
    return initial.length > 0 ? initial : [''];
  });

  // Фотографии: новые файлы, их превью, и удалённые ID
  const [newImagesFiles, setNewImagesFiles] = useState([]);
  const [newImagePreviews, setNewImagePreviews] = useState([]);
  const [deletedImageIds, setDeletedImageIds] = useState([]);
  const fileInputRef = useRef(null);

  // Лайтбокс для просмотра картинок
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState('');

  // Refs для автоподгонки размеров текстовых блоков
  const partsRefs = useRef({});

  // Модалка отмены
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Навигация с подтверждением при переключении через Sidebar
  const [pendingNavPath, setPendingNavPath] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // ────────────────────────────────────────────────────────────────────────────────
  // 1) Загрузка всех конструктивных решений (для Select) и установка selectedDesign
  useEffect(() => {
    fetch(`${API_BASE_URL}/collector/get_all_design`)
      .then(res => {
        if (!res.ok) throw new Error(res.status);
        return res.json();
      })
      .then(data => {
        const opts = data.map(d => ({
          value: d.id,
          label: d.collector_design,
        }));
        setDesignOptions(opts);

        // Устанавливаем текущее из passedCollector.collector_design_id
        const current = opts.find(o => o.value === passedCollector?.collector_design_id);
        setSelectedDesign(current || null);
      })
      .catch(console.error);
  }, [passedCollector?.collector_design_id]);

  // 2) Когда selectedDesign или passedCollector меняются, загружаем designData
  useEffect(() => {
    const designId = selectedDesign?.value || passedCollector?.collector_design_id;
    if (!designId) return;

    fetch(`${API_BASE_URL}/collector/get_all_design`)
      .then(res => {
        if (!res.ok) throw new Error(res.status);
        return res.json();
      })
      .then(list => {
        const found = list.find(d => d.id === designId);
        if (found) {
          setDesignData(found);
        }
      })
      .catch(console.error);
  }, [selectedDesign, passedCollector?.collector_design_id]);

  // 3) Авто-рост textarea при изменении блоков
  useEffect(() => {
    Object.values(partsRefs.current).forEach(autoGrow);
  }, [diameterBlocks, nodeBlocks, conditionBlocks]);

  // 4) Слушатель попыток навигации из Sidebar
  useEffect(() => {
    const onNavigateAttempt = (e) => {
      setPendingNavPath(e.detail);
      setShowConfirm(true);
    };
    window.addEventListener('navigateAttempt', onNavigateAttempt);
    return () => {
      window.removeEventListener('navigateAttempt', onNavigateAttempt);
    };
  }, []);

  // Если passedCollector отсутствует — не рендерим
  if (!passedCollector) return null;

  // Утилита: вместо пустого — тире
  const orDash = val => (val == null || String(val).trim() === '' ? '-' : val);

  // Обработчик открытия выбора файлов
  const handleAddPhotoClick = () => {
    fileInputRef.current?.click();
  };

  // Обработка добавления новых файлов
  const handleFileChange = e => {
    const chosenFiles = Array.from(e.target.files).filter(f =>
      f.type.startsWith('image/')
    );
    if (chosenFiles.length === 0) {
      e.target.value = null;
      return;
    }

    // Избегаем дублирования по имени+размеру
    setNewImagesFiles(prevFiles => {
      const existingIds = new Set(prevFiles.map(f => `${f.name}_${f.size}`));
      const unique = chosenFiles.filter(f => {
        const id = `${f.name}_${f.size}`;
        return !existingIds.has(id);
      });
      if (unique.length < chosenFiles.length) {
        alert('Некоторые файлы уже были добавлены и были проигнорированы');
      }
      return [...prevFiles, ...unique];
    });

    setNewImagePreviews(prevPreviews => {
      const existingUrls = new Set(prevPreviews);
      const newUrls = chosenFiles
        .map(file => URL.createObjectURL(file))
        .filter(u => !existingUrls.has(u));
      return [...prevPreviews, ...newUrls];
    });

    e.target.value = null;
  };

  // Удаление нового (еще не загруженного) файла
  const handleDeleteNew = idx => {
    setNewImagesFiles(prev => prev.filter((_, i) => i !== idx));
    setNewImagePreviews(prev => prev.filter((_, i) => i !== idx));
  };

  // Пометка существующей фотографии на удаление
  const handleMarkDeleteExisting = imageId => {
    setDeletedImageIds(prev => [...prev, imageId]);
  };

  // Сохранение и отправка данных на сервер без навигации
  const handleSave = async () => {
    const designId = selectedDesign?.value || passedCollector.collector_design_id;

    // 1. Собрать строчки из блочных полей
    const diameterStr = encodeURIComponent(formatBlocks(diameterBlocks));
    const nodeStr = encodeURIComponent(formatBlocks(nodeBlocks));
    const conditionStr = encodeURIComponent(formatBlocks(conditionBlocks));

    // 2. Сформировать URL для PUT /collector/update_collector/{collector_id}
    const url =
      `${API_BASE_URL}/collector/update_collector/${passedCollector.id}` +
      `?name=${encodeURIComponent(name)}` +
      `&collector_design_id=${designId}` +
      `&year=${encodeURIComponent(year)}` +
      `&material=${encodeURIComponent(material)}` +
      `&length=${encodeURIComponent(length)}` +
      `&diameter=${diameterStr}` +
      `&node_flow=${nodeStr}` +
      `&expenditure=${encodeURIComponent(expenditure)}` +
      `&technical_condition=${conditionStr}` +
      `&latitude=${encodeURIComponent(passedCollector.latitude)}` +
      `&longitude=${encodeURIComponent(passedCollector.longitude)}`;

    // 3. Выполнить PUT
    try {
      const res = await fetch(url, { method: 'PUT' });
      if (!res.ok) {
        console.error(`Ошибка при обновлении текста коллектора: ${res.status}`);
        alert('Не удалось сохранить изменения текста');
        return;
      }
    } catch (err) {
      console.error('Ошибка при запросе на update_collector:', err);
      alert('Не удалось сохранить изменения текста');
      return;
    }

    // 4. Удаление отмеченных фотографий
    if (deletedImageIds.length > 0) {
      for (const imageId of deletedImageIds) {
        try {
          const deleteUrl = `${API_BASE_URL}/collector/delete_design_image/${designId}/images/${imageId}`;
          const resp = await fetch(deleteUrl, { method: 'DELETE' });
          if (!resp.ok) {
            console.error(`Ошибка при удалении фото ${imageId}: ${resp.status}`);
          }
        } catch (err) {
          console.error(`Ошибка при удалении фото ${imageId}:`, err);
        }
      }
    }

    // 5. Загрузка новых фотографий
    if (newImagesFiles.length > 0) {
      const formData = new FormData();
      newImagesFiles.forEach(file => formData.append('images', file));

      try {
        const postUrl = `${API_BASE_URL}/collector/add_design/${designId}/images`;
        const uploadRes = await fetch(postUrl, {
          method: 'POST',
          body: formData,
        });
        if (!uploadRes.ok) {
          console.error(`Ошибка при загрузке фото: ${uploadRes.status}`);
          alert('Не удалось загрузить новые фотографии');
          return;
        }
      } catch (err) {
        console.error('Ошибка при загрузке фото:', err);
        alert('Не удалось загрузить новые фотографии');
        return;
      }
    }

    // 6. Подготовить обновлённый объект коллектора для передачи в state
    const updatedCollector = {
      ...passedCollector,
      name,
      year,
      material,
      length,
      diameter: formatBlocks(diameterBlocks),
      node_flow: formatBlocks(nodeBlocks),
      technical_condition: formatBlocks(conditionBlocks),
      collector_design_id: designId,
      source: source === 'map' ? 'map' : 'db',
      design: {
        collector_design: selectedDesign?.label || passedCollector.design?.collector_design,
      },
    };

    // 7. Перейти на страницу деталей, передав обновлённый объект и source
    navigate(`/collectors/${passedCollector.id}`, {
      state: { collectorData: updatedCollector, source: updatedCollector.source },
    });
  };

  // Сохранение и навигация при подтверждении из модалки
  const handleSaveAndNavigate = async (path) => {
    const designId = selectedDesign?.value || passedCollector.collector_design_id;

    // Повторяем логику сохранения
    const diameterStr = encodeURIComponent(formatBlocks(diameterBlocks));
    const nodeStr = encodeURIComponent(formatBlocks(nodeBlocks));
    const conditionStr = encodeURIComponent(formatBlocks(conditionBlocks));
    const url =
      `${API_BASE_URL}/collector/update_collector/${passedCollector.id}` +
      `?name=${encodeURIComponent(name)}` +
      `&collector_design_id=${designId}` +
      `&year=${encodeURIComponent(year)}` +
      `&material=${encodeURIComponent(material)}` +
      `&length=${encodeURIComponent(length)}` +
      `&diameter=${diameterStr}` +
      `&node_flow=${nodeStr}` +
      `&expenditure=${encodeURIComponent(expenditure)}` +
      `&technical_condition=${conditionStr}` +
      `&latitude=${encodeURIComponent(passedCollector.latitude)}` +
      `&longitude=${encodeURIComponent(passedCollector.longitude)}`;

    try {
      const res = await fetch(url, { method: 'PUT' });
      if (!res.ok) {
        console.error(`Ошибка при обновлении текста коллектора: ${res.status}`);
        alert('Не удалось сохранить изменения текста');
        return;
      }
    } catch (err) {
      console.error('Ошибка при запросе на update_collector:', err);
      alert('Не удалось сохранить изменения текста');
      return;
    }

    if (deletedImageIds.length > 0) {
      for (const imageId of deletedImageIds) {
        try {
          const deleteUrl = `${API_BASE_URL}/collector/delete_design_image/${designId}/images/${imageId}`;
          const resp = await fetch(deleteUrl, { method: 'DELETE' });
          if (!resp.ok) {
            console.error(`Ошибка при удалении фото ${imageId}: ${resp.status}`);
          }
        } catch (err) {
          console.error(`Ошибка при удалении фото ${imageId}:`, err);
        }
      }
    }

    if (newImagesFiles.length > 0) {
      const formData = new FormData();
      newImagesFiles.forEach(file => formData.append('images', file));

      try {
        const postUrl = `${API_BASE_URL}/collector/add_design/${designId}/images`;
        const uploadRes = await fetch(postUrl, {
          method: 'POST',
          body: formData,
        });
        if (!uploadRes.ok) {
          console.error(`Ошибка при загрузке фото: ${uploadRes.status}`);
          alert('Не удалось загрузить новые фотографии');
          return;
        }
      } catch (err) {
        console.error('Ошибка при загрузке фото:', err);
        alert('Не удалось загрузить новые фотографии');
        return;
      }
    }

    const updatedCollector = {
      ...passedCollector,
      name,
      year,
      material,
      length,
      diameter: formatBlocks(diameterBlocks),
      node_flow: formatBlocks(nodeBlocks),
      technical_condition: formatBlocks(conditionBlocks),
      collector_design_id: designId,
      source: source === 'map' ? 'map' : 'db',
      design: {
        collector_design: selectedDesign?.label || passedCollector.design?.collector_design,
      },
    };

    navigate(path, {
      state: { collectorData: updatedCollector, source: updatedCollector.source },
    });
  };

  return (
    <div className="edit-house-page">
      {/* Заголовок */}
      <div className="edit-header-home">
        <button
          className="back-btn-home"
          onClick={() => setShowCancelModal(true)}
        >
          <span className="back-icon-home">⌄</span>
        </button>
        <h1 className="edit-title">
          Редактирование коллектора:{' '}
          <span className="edit-address">{orDash(name)}</span>
        </h1>
        <div className="edit-actions">
          <button
            className="primary-btn cancel"
            onClick={() => setShowCancelModal(true)}
          >
            Отменить
          </button>
          <button className="primary-btn save" onClick={handleSave}>
            Сохранить
          </button>
        </div>
      </div>

      {/* Модалка отмены */}
      {showCancelModal && (
        <CancelEditingModal
          onClose={() => setShowCancelModal(false)}
          onConfirm={() => {
            setShowCancelModal(false);
            // При отмене возвращаемся на страницу деталей коллектора
            navigate(`/collectors/${passedCollector.id}`, {
              state: { collectorData: passedCollector, source },
            });
          }}
        />
      )}

      {/* Тело страницы */}
      <div className="edit-house-body">
        {/* Левая панель: поля коллектора */}
        <div className="edit-house-left">
          <div className="edit-house-table-container">
            <div className="edit-house-table-wrapper">
              <table className="edit-house-table">
                <tbody>
                  <tr className="edit-house-table__row">
                    <td className="edit-house-table__cell edit-house-table__cell--label">
                      Год ввода
                    </td>
                    <td className="edit-house-table__cell edit-house-table__cell--value">
                      <input
                        type="text"
                        value={year}
                        onChange={e => setYear(e.target.value)}
                      />
                    </td>
                  </tr>
                  <tr className="edit-house-table__row">
                    <td className="edit-house-table__cell edit-house-table__cell--label">
                      Название коллектора
                    </td>
                    <td className="edit-house-table__cell edit-house-table__cell--value">
                      <input
                        type="text"
                        value={name}
                        onChange={e => setName(e.target.value)}
                      />
                    </td>
                  </tr>
                  <tr className="edit-house-table__row">
                    <td className="edit-house-table__cell edit-house-table__cell--label">
                      Конструктивное решение
                    </td>
                    <td className="edit-house-table__cell edit-house-table__cell--value">
                      <Select
                        options={designOptions}
                        value={selectedDesign}
                        onChange={setSelectedDesign}
                        className="edit-series-select"
                        classNamePrefix="edit-series"
                        isSearchable
                        menuPortalTarget={document.body}
                        menuPosition="absolute"
                        styles={{
                          control: base => ({
                            ...base,
                            backgroundColor: '#15171D',
                            borderColor: '#3B3B40',
                            borderRadius: '4px',
                            minHeight: '36px',
                          }),
                          singleValue: base => ({ ...base, color: '#e5e7eb' }),
                          placeholder: base => ({ ...base, color: '#8b919d' }),
                          menu: base => ({
                            ...base,
                            backgroundColor: '#2a2c33',
                            borderColor: '#3B3B40',
                            marginTop: '4px',
                          }),
                          option: (base, state) => ({
                            ...base,
                            backgroundColor: state.isFocused
                              ? '#3B3B40'
                              : '#2a2c33',
                            color: '#e5e7eb',
                          }),
                          menuPortal: base => ({ ...base, zIndex: 9999 }),
                        }}
                      />
                    </td>
                  </tr>
                  <tr className="edit-house-table__row">
                    <td className="edit-house-table__cell edit-house-table__cell--label">
                      Материал
                    </td>
                    <td className="edit-house-table__cell edit-house-table__cell--value">
                      <input
                        type="text"
                        value={material}
                        onChange={e => setMaterial(e.target.value)}
                      />
                    </td>
                  </tr>
                  <tr className="edit-house-table__row">
                    <td className="edit-house-table__cell edit-house-table__cell--label">
                      Длина
                    </td>
                    <td className="edit-house-table__cell edit-house-table__cell--value">
                      <input
                        type="text"
                        value={length}
                        onChange={e => setLength(e.target.value)}
                      />
                    </td>
                  </tr>
                  <tr className="edit-house-table__row">
                    <td className="edit-house-table__cell edit-house-table__cell--label">
                      Расход на участке
                    </td>
                    <td className="edit-house-table__cell edit-house-table__cell--value">
                      <input
                        type="text"
                        value={expenditure}
                        onChange={e => setExpenditure(e.target.value)}
                      />
                    </td>
                  </tr>

                  {/* Диаметр */}
                  <tr className="edit-house-table__row edit-house-desc-row">
                    <td className="edit-house-table__cell edit-house-table__cell--label">
                      Диаметр
                    </td>
                    <td className="edit-house-table__cell edit-house-desc-cell">
                      <div className="description-parts">
                        {diameterBlocks.map((_, i) => (
                          <textarea
                            key={`d-${i}`}
                            ref={el => (partsRefs.current[`d-${i}`] = el)}
                            className="edit-house-textarea"
                            value={diameterBlocks[i]}
                            placeholder={
                              diameterBlocks[i] === ''
                                ? 'Если вы хотите поставить новый абзац, то перед ним поставьте @@'
                                : ''
                            }
                            onChange={e => {
                              const newArr = [...diameterBlocks];
                              newArr[i] = e.target.value;
                              setDiameterBlocks(newArr);
                            }}
                          />
                        ))}
                      </div>
                    </td>
                  </tr>

                  {/* Узловые расходы */}
                  <tr className="edit-house-table__row edit-house-desc-row">
                    <td className="edit-house-table__cell edit-house-table__cell--label">
                      Узловые расходы
                    </td>
                    <td className="edit-house-table__cell edit-house-table__cell--desc edit-house-desc-cell">
                      <div className="description-parts">
                        {nodeBlocks.map((_, i) => (
                          <textarea
                            key={`n-${i}`}
                            ref={el => (partsRefs.current[`n-${i}`] = el)}
                            className="edit-house-textarea"
                            value={nodeBlocks[i]}
                            placeholder={
                              nodeBlocks[i] === ''
                                ? 'Если вы хотите поставить новый абзац, то перед ним поставьте @@'
                                : ''
                            }
                            onChange={e => {
                              const newArr = [...nodeBlocks];
                              newArr[i] = e.target.value;
                              setNodeBlocks(newArr);
                            }}
                          />
                        ))}
                      </div>
                    </td>
                  </tr>

                  {/* Техническое состояние */}
                  <tr className="edit-house-table__row edit-house-desc-row">
                    <td className="edit-house-table__cell edit-house-table__cell--label">
                      Техническое состояние
                    </td>
                    <td className="edit-house-table__cell edit-house-table__cell--desc edit-house-desc-cell">
                      <div className="description-parts">
                        {conditionBlocks.map((_, i) => (
                          <textarea
                            key={`c-${i}`}
                            ref={el => (partsRefs.current[`c-${i}`] = el)}
                            className="edit-house-textarea"
                            value={conditionBlocks[i]}
                            placeholder={
                              conditionBlocks[i] === ''
                                ? 'Если вы хотите поставить новый абзац, то перед ним поставьте @@'
                                : ''
                            }
                            onChange={e => {
                              const newArr = [...conditionBlocks];
                              newArr[i] = e.target.value;
                              setConditionBlocks(newArr);
                            }}
                          />
                        ))}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Разделитель */}
        <div className="edit-divider" />

        {/* Правая панель: галерея чертежей (фотографий) */}
        <div className="edit-house-right">
          {/* Скрытый input[type="file"] */}
          <input
            type="file"
            accept="image/*"
            multiple
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />

          <div className="collector-photo-gallery">
            {/* Кнопка добавления новых изображений */}
            <div className="collector-photo-item">
              <button
                className="collector-add-photo-btn primary-btn-save"
                onClick={handleAddPhotoClick}
              >
                Добавить чертёж +
              </button>
            </div>

            {/* Превью новых изображений (еще не загруженных) */}
            {newImagePreviews.map((src, i) => (
              <div key={`new-${i}`} className="collector-photo-item">
                <span
                  className="collector-photo-close"
                  onClick={() => handleDeleteNew(i)}
                >
                  &times;
                </span>
                <img
                  src={src}
                  alt={`Новый чертёж ${i + 1}`}
                  className="clickable-image"
                  onClick={() => {
                    setLightboxSrc(src);
                    setLightboxOpen(true);
                  }}
                />
              </div>
            ))}

            {/* Существующие (сохранённые) изображения */}
            {designData?.images
              ?.filter(img => !deletedImageIds.includes(img.id))
              .map((img, i) => (
                <div key={img.id} className="collector-photo-item">
                  <span
                    className="collector-photo-close"
                    onClick={() => handleMarkDeleteExisting(img.id)}
                  >
                    ×
                  </span>
                  <img
                    src={img.url}
                    alt={`Чертёж ${i + 1}`}
                    className="clickable-image"
                    onClick={() => {
                      setLightboxSrc(img.url);
                      setLightboxOpen(true);
                    }}
                  />
                </div>
              ))}

            {/* Если нет ни новых, ни существующих изображений */}
            {!(
              newImagePreviews.length > 0 ||
              (designData?.images?.filter(img => !deletedImageIds.includes(img.id)) || []).length
            ) && <div className="no-drawings-message">Здесь ещё нет чертежей</div>}
          </div>
        </div>
      </div>

      {/* Лайтбокс */}
      <Modal
        isOpen={lightboxOpen}
        onRequestClose={() => setLightboxOpen(false)}
        className="lightbox-modal"
        overlayClassName="lightbox-overlay"
      >
        <img
          src={lightboxSrc}
          alt="Чертёж во весь экран"
          className="lightbox-image"
          onClick={() => setLightboxOpen(false)}
        />
      </Modal>

      {/* Модалка сохранения при навигации */}
      {showConfirm && pendingNavPath && (
        <SaveChangesModal
          onClose={() => {
            setShowConfirm(false);
            setPendingNavPath(null);
          }}
          onConfirm={() => {
            setShowConfirm(false);
            handleSaveAndNavigate(pendingNavPath);
            setPendingNavPath(null);
          }}
        />
      )}
    </div>
  );
};

export default EditCollectorsDetails;
